import { useQueryClient } from '@tanstack/react-query';
import { toMentionPlainText } from '@terminal/components/chat/chat-input/mentions/markers';
import { copy } from '@terminal/components/copy-to-clipboard/utils/copy';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useState } from 'react';

import { ConversationTask } from '../use-chat/use-chat.types';
import { useVisualizationStore } from '../use-visualization-store/useVisualizationStore';
import { CopyAction, copyResearchMessage } from '../utils/copyResearchMessage';
import { useConversationInventory } from './useConversationInventory';
import { fetchFormattedInventory } from './useFormattedInventory';

interface UseCopyConversationParams {
  task: ConversationTask;
  questionOnly: boolean;
  showingSteps: boolean;
  conversationId: string;
  action: CopyAction;
}

export const useCopyConversation = ({
  task,
  questionOnly,
  showingSteps,
  conversationId,
  action,
}: UseCopyConversationParams) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackAlfredChatCopy, trackAlfredConversationPrint } =
    usePosthogEvents();
  const mentionsEnabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2103-alfred-mentions')
  );

  const { data: inventory } = useConversationInventory(conversationId, true);
  const queryClient = useQueryClient();

  const { charts, tables } = useVisualizationStore(state => ({
    charts: state.charts,
    tables: state.tables,
  }));

  const markCopied = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2_000);
  };

  const handleCopy = async () => {
    if (action === 'copy') {
      trackAlfredChatCopy(POSTHOG_TRACKING_SOURCES.ALFRED);
    } else if (action === 'print') {
      trackAlfredConversationPrint(POSTHOG_TRACKING_SOURCES.ALFRED);
    }

    const plainQuestion = toMentionPlainText(task.input, mentionsEnabled);

    if (questionOnly) {
      copy(plainQuestion, plainQuestion);
      markCopied();
      return;
    }

    const states = task.states;
    if (!states?.length) {
      return;
    }

    setIsLoading(true);
    try {
      const formattedInventory = await fetchFormattedInventory(
        inventory,
        queryClient
      );
      await copyResearchMessage({
        action,
        task: showingSteps
          ? task
          : { ...task, states: [states[states.length - 1]] },
        charts,
        tables,
        inventory: formattedInventory,
        taskQuestion: plainQuestion,
      });

      markCopied();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isCopied,
    isLoading,
    handleCopy,
  };
};
